import dayjs from 'dayjs'
import React from 'react'
import { Tag, Tooltip } from 'antd'
import { DataType } from './interface'
import { EditableColumnType } from '../../../components/data-table/interface'
import { PaperClipOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { formatNumber, formatRoundedNumber, formatRouteSheetLoadingTime } from './utils'

const ColumnId: EditableColumnType<DataType>[] = [
  /*{
    title: 'ID',
    dataIndex: 'id',
    key: 'id',
    width: 50,
    fixed: 'left',
    render: (val: string) => {
      const style: React.CSSProperties = val
        ? {
            textAlign: 'center',
            fontWeight: 'bold',
          }
        : {}
      return <div style={style}>{val || ''}</div>
    },
  },*/
]

const ColumnsGeneral: EditableColumnType<DataType>[] = [
  {
    title: 'Дата поездки',
    dataIndex: 'date',
    key: 'date',
    width: 100,
    sorter: true,
    render: val => {
      if (!val) return val
      const date = dayjs(val)
      return date.isValid() ? date.format('DD.MM.YYYY') : val
    },
  },
  {
    title: 'Тип',
    dataIndex: 'source',
    key: 'source',
    width: 80,
    render: value => (value === 'manual' ? <Tag color="gold">Ручной</Tag> : ''),
  },
  {
    title: 'Машина',
    dataIndex: 'vehicle',
    key: 'vehicle',
    sorter: true,
    width: 130,
  },
  {
    title: 'Перевозчик',
    dataIndex: 'carrier_name',
    key: 'carrier_name',
    sorter: true,
    width: 140,
    render: val => val || '',
  },
  {
    title: 'Группы',
    dataIndex: 'zone_groups',
    key: 'zone_groups',
    sorter: true,
    width: 100,
  },
  {
    title: 'Водитель',
    dataIndex: 'driver_name',
    key: 'driver_name',
    sorter: true,
    width: 150,
  },
  {
    title: 'Расстояние, км',
    dataIndex: 'distance',
    key: 'distance',
    sorter: true,
    editable: true,
    width: 100,
    render: val => formatRoundedNumber(val, ''),
  },
  {
    title: 'Кол-во изделий',
    dataIndex: 'total_items_count',
    key: 'total_items_count',
    sorter: true,
    width: 70,
    render: val => formatNumber(val, ''),
  },
  {
    title: 'Кол-во стеклопакетов',
    dataIndex: 'total_glass_units_count',
    key: 'total_glass_units_count',
    sorter: true,
    width: 95,
    render: val => formatNumber(val, ''),
  },
  {
    title: 'Пирамида',
    dataIndex: 'pyramid_numbers',
    key: 'pyramid_numbers',
    sorter: true,
    width: 70,
    render: val => (val && Array.isArray(val) ? val.join(', ') : val),
  },
  {
    title: 'Фото',
    dataIndex: 'has_files',
    key: 'has_files',
    width: 50,
    render: val => (val ? <PaperClipOutlined style={{ fontSize: 16 }} /> : ''),
  },
  {
    title: 'Вес рассчитанный, кг',
    dataIndex: 'total_weight',
    key: 'total_weight',
    sorter: true,
    width: 100,
    render: (val, record) => {
      const calculatedWeight = Number(val) || 0
      const actualWeight = Number(record.total_weight_fact) || 0
      const style: React.CSSProperties =
        calculatedWeight < actualWeight ? { fontWeight: 'bold', color: 'red' } : {}
      return <span style={style}>{formatNumber(val, '')}</span>
    },
  },
  {
    title: 'Вес фактический, кг',
    dataIndex: 'total_weight_fact',
    key: 'total_weight_fact',
    sorter: true,
    editable: true,
    width: 100,
    render: (val, record) => {
      const calculatedWeight = Number(record.total_weight) || 0
      const actualWeight = Number(val) || 0
      const style: React.CSSProperties =
        calculatedWeight < actualWeight ? { fontWeight: 'bold', color: 'red' } : {}
      return <span style={style}>{formatNumber(val, '')}</span>
    },
  },
  {
    title: 'Площадь, м²',
    dataIndex: 'total_volume',
    key: 'total_volume',
    sorter: true,
    width: 110,
    render: val => formatNumber(val, ''),
  },
  {
    title: 'Оплата клиентов, руб.',
    dataIndex: 'total_delivery_amount',
    key: 'total_delivery_amount',
    sorter: true,
    width: 120,
    render: val => formatNumber(val, ''),
  },
  {
    exportTitle: 'Оплата перевозчикам, руб.',
    title: (
      <Tooltip title="Стоимость за километр × расстояние + доплата грузчикам">
        <span>
          Оплата перевозчикам, руб. <QuestionCircleOutlined style={{ marginLeft: 0 }} />
        </span>
      </Tooltip>
    ),
    dataIndex: 'carrier_cost',
    key: 'carrier_cost',
    sorter: true,
    width: 130,
    render: val => formatNumber(val, ''),
  },
  {
    title: 'Эффективность',
    dataIndex: 'efficiency',
    key: 'efficiency',
    sorter: true,
    width: 90,
    render: val => {
      const style: React.CSSProperties = val < 1 ? { fontWeight: 'bold', color: 'red' } : {}
      return val ? <span style={style}>{val}</span> : '0'
    },
  },
  {
    title: 'Маржа, руб.',
    dataIndex: 'margin',
    key: 'margin',
    sorter: true,
    width: 120,
    render: val => {
      const margin = Number(val) || 0
      const style: React.CSSProperties = margin < 0 ? { fontWeight: 'bold', color: 'red' } : {}
      return <span style={style}>{formatNumber(margin, '')}</span>
    },
  },
  {
    title: 'Доплата грузчикам, руб.',
    dataIndex: 'loader_amount',
    key: 'loader_amount',
    sorter: true,
    editable: true,
    width: 120,
    render: val => formatNumber(val, ''),
  },
  {
    title: 'Прочие расходы, руб.',
    dataIndex: 'other_amount',
    key: 'other_amount',
    sorter: true,
    editable: true,
    width: 120,
    render: val => formatNumber(val, ''),
  },
  {
    title: 'Затраты логистики, руб.',
    dataIndex: 'total_logistics_amount',
    key: 'total_logistics_amount',
    sorter: true,
    editable: true,
    width: 120,
    render: val => formatNumber(val, ''),
  },
  {
    title: 'Загрузка',
    dataIndex: 'loading_time',
    key: 'loading_time',
    sorter: true,
    width: 100,
    render: val => formatRouteSheetLoadingTime(val),
  },
  {
    title: 'Кол-во заказов',
    dataIndex: 'orders_count',
    key: 'orders_count',
    sorter: true,
    width: 70,
    render: val => formatNumber(val, ''),
  },
  {
    title: 'Кол-во адресов',
    dataIndex: 'unique_addresses_count',
    key: 'unique_addresses_count',
    sorter: true,
    width: 70,
    render: val => formatNumber(val, ''),
  },
  {
    title: 'Склад',
    dataIndex: 'center_name',
    key: 'center_name',
    sorter: true,
    width: 70,
  },
]

export const getColumns = (leader, sorter) => {
  const columns = leader ? ColumnId.concat(ColumnsGeneral) : ColumnId.concat(ColumnsGeneral)
  return columns.map(item => {
    if (item.key === sorter.sort_field) item.sortOrder = sorter.sort_order
    else delete item.sortOrder
    return item
  })
}
